import React, { useMemo } from 'react';
import "./bookingFeesSection.css";
import { useZohoOrgId, zohoInvoiceUrl } from '../../../../utils/zohoOrg';

export default function BookingFeesSection({
    booking,
    canManageInvoice, // managers + supervisors — may GENERATE from this page
    canViewInvoice,   // managers + supervisors — may see the invoice status
    canOpenDrawer,    // managers + supervisors — badge opens the drawer
    onGenerateInvoice,
    onOpenInvoice,    // badge click: drawer for manager/supervisor, Zoho for admin
    onGenerateGuestInvoice, // Round 7: invoice a one-time GUEST instead of the client
}) {
    const zohoOrgId = useZohoOrgId();
    const formatMoney = (val) => {
        const n = Number(val || 0);
        const s = n.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
        return `AED ${s}`;
    };

    // item_id -> override, so each row can say HOW it was overridden —
    // "the booking fees can't show which items and how they have been
    // overwritten" (2026-08-03). The chip in the header stays as the
    // summary; this is the per-line truth.
    const overridesByItem = useMemo(() => {
        const map = {};
        for (const o of (booking?.price_overrides || [])) map[o.item_id] = o;
        return map;
    }, [booking?.price_overrides]);

    const priceItems = useMemo(() => {
        const items = booking?.price_items;
        return Array.isArray(items) ? items : [];
    }, [booking?.price_items]);

    const subtotal = Number(booking?.subtotal_amount || 0);
    const vat = Number(booking?.vat_amount || 0);
    const totalWithVat = Number(booking?.total_with_vat || 0);

    const showBreakdown = priceItems.length > 0;

    const hasAnyDiscount = useMemo(
        () => priceItems.some(it => Number(it.discount_percent ?? it.discount ?? 0) > 0),
        [priceItems]
    );

    const hasAnyTax = useMemo(
        () => priceItems.some(it => Number(it.tax_amount ?? 0) > 0),
        [priceItems]
    );

    const isInvoiced = !!(booking?.is_invoiced || booking?.invoice_id || booking?.zoho_invoice_id);
    // Show the PRETTY invoice number (INV-000123), never the long internal Zoho id.
    const invoiceNumber = booking?.zoho_invoice_number || null;
    // "Invoiced" alone says a bill exists, not whether it was ever paid — the
    // one thing anyone reading this row actually wants (2026-08-30). The
    // booking payload already carries the invoice's live status; use it.
    const invStatus = String(booking?.invoice_status || '').toLowerCase();
    const INV_STATE = {
        paid: 'Paid',
        partially_paid: 'Part paid',
        overdue: 'Overdue',
        draft: 'Draft',
        void: 'Void',
    };
    const invState = INV_STATE[invStatus] || null;
    const invoicedLabel = `Invoiced${invoiceNumber ? ` • ${invoiceNumber}` : ''}`
        + (invState ? ` — ${invState}` : '');
    const invStateClass = invStatus === 'paid' ? ' is-paid'
        : invStatus === 'overdue' ? ' is-overdue'
        : invStatus === 'partially_paid' ? ' is-part' : '';

    return (
        <section className="dp-card booking-section booking-fees-section booking-fees-full">
            <div className="booking-fees-header dp-cardHead">
                <h3>Booking Fees</h3>
                {/* Overrides show HERE, not as a second table (the owner
                    2026-08-03): the row prices below are already the
                    overridden ones — this chip says why, with the manager's
                    reason. Editing lives in the Edit Booking modal. */}
                {booking?.has_price_override && (
                    <span
                        className="fees-override-chip"
                        title={(booking.price_overrides || [])
                            .map(o => `${o.reason ? `“${o.reason}” — ` : ''}set by ${o.created_by_name || 'a manager'}`)
                            .join('\n') || 'A manager has overridden this booking\u2019s price'}
                    >
                        Price override{(booking.price_overrides || []).some(o => o.reason)
                            ? ` — ${(booking.price_overrides || []).find(o => o.reason)?.reason}`
                            : ''}
                    </span>
                )}

                {/* ONE right-aligned action group (2026-08-09): Guest sits
                    LEFT of Generate, Open Invoice joins when invoiced — no
                    buttons floating mid-header. */}
                <div className="booking-fees-actions">
                {canManageInvoice && !isInvoiced && onGenerateGuestInvoice && (
                    <button
                        className="fees-guest-invoice-btn"
                        onClick={onGenerateGuestInvoice}
                        title="Invoice a one-time guest instead of the client"
                    >
                        Generate Guest Invoice
                    </button>
                )}
                {/* Once invoiced, Generate disappears — re-generating lives in
                    the drawer (delete → generate), never here (2026-08-09). */}
                {canManageInvoice && !isInvoiced && (
                    <button
                        className="generate-invoice-button"
                        onClick={onGenerateInvoice}
                        title="Generate invoice"
                    >
                        Generate Invoice
                    </button>
                )}
                {/* Open Invoice — ALL staff roles; drawer for everyone (admins
                    view-only inside; delete manager-only). Zoho anchor only for
                    an invoice with no local row. */}
                {isInvoiced && (
                    booking?.invoice_id ? (
                        <button
                            className="fees-open-invoice-btn"
                            onClick={onOpenInvoice}
                            title="Open invoice details"
                        >
                            Open Invoice
                        </button>
                    ) : booking?.zoho_invoice_id && zohoOrgId ? (
                        <a
                            className="fees-open-invoice-btn"
                            href={zohoInvoiceUrl(zohoOrgId, booking.zoho_invoice_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open this invoice in Zoho Books"
                        >
                            Open Invoice
                        </a>
                    ) : (
                        <button
                            className="fees-open-invoice-btn"
                            disabled
                            title="Invoice link unavailable"
                        >
                            Open Invoice
                        </button>
                    )
                )}
                </div>
            </div>

            {canViewInvoice && (
                <div className="invoice-status-row">
                    <strong>Invoice:</strong>{' '}
                    {isInvoiced ? (
                        // The badge is clickable for anyone who can view an invoice.
                        // Managers and supervisors get the drawer; an admin gets the
                        // invoice itself in Zoho, which is the view access they had
                        // before (2026-07-27).
                        canOpenDrawer && booking?.invoice_id ? (
                            <button
                                type="button"
                                className={`invoice-status invoiced${invStateClass}`}
                                onClick={onOpenInvoice}
                                title="Open invoice details"
                            >
                                {invoicedLabel}
                            </button>
                        ) : booking?.zoho_invoice_id && zohoOrgId ? (
                            <a
                                className={`invoice-status invoiced${invStateClass}`}
                                href={zohoInvoiceUrl(zohoOrgId, booking.zoho_invoice_id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Open this invoice in Zoho Books"
                            >
                                {invoicedLabel}
                            </a>
                        ) : (
                            <span className={`invoice-status invoiced${invStateClass}`}>{invoicedLabel}</span>
                        )
                    ) : (
                        <span className="invoice-status not-invoiced">Not invoiced yet</span>
                    )}
                </div>
            )}

            {showBreakdown ? (
                <>
                    <div className="fees-table">
                        <div
                            className="fees-head"
                            style={{
                                gridTemplateColumns: hasAnyDiscount
                                    ? '1fr 90px 110px 110px 140px 140px 140px'
                                    : '1fr 90px 110px 140px 140px 140px'
                            }}
                        >
                            <div>Item</div>
                            <div className="fees-right">Qty</div>
                            <div className="fees-right">Unit Price</div>
                            {hasAnyDiscount && <div className="fees-right">Discount</div>}
                            <div className="fees-right">Taxable Amount</div>
                            <div className="fees-right">Tax</div>
                            <div className="fees-right fees-total">Line Total</div>
                        </div>

                        {priceItems.map((it, idx) => {
                            const qty = Number(it.quantity || 0);
                            // Set by hand: show what it would have been, struck
                            // through, beside what is billed (2026-08-30).
                            const qtyWas = it.computed_quantity == null
                                ? null : Number(it.computed_quantity);
                            const unit = Number(it.unit_price || 0);
                            const discPct = Number(it.discount_percent ?? it.discount ?? 0);

                            const taxableAmount = Number(it.line_subtotal ?? (qty * unit));
                            const taxAmount = Number(it.tax_amount ?? 0);
                            const taxRate = Number(it.tax_rate ?? 0);
                            const lineTotal = Number(it.line_total ?? 0);

                            return (
                                <div
                                    key={`${it.item_id || 'x'}-${idx}`}
                                    className="fees-row"
                                    style={{
                                        gridTemplateColumns: hasAnyDiscount
                                            ? '1fr 90px 110px 110px 140px 140px 140px'
                                            : '1fr 90px 110px 140px 140px 140px'
                                    }}
                                >
                                    <div className="fees-item">
                                        <div className="fees-item-name">{it.name || it.description || 'Item'}</div>
                                        {it.description && it.name && it.description !== it.name ? (
                                            <div className="fees-item-desc">{it.description}</div>
                                        ) : null}
                                        {overridesByItem[it.item_id] && (
                                            <div className="fees-override-how">
                                                {/* Say which kind it actually is. "Not a
                                                    percent" stopped meaning "a rate" once
                                                    quantity overrides existed, and a
                                                    quantity-only line read "Custom rate"
                                                    over an untouched price (2026-08-30). */}
                                                {overridesByItem[it.item_id].discount_percent != null
                                                    ? `${String(parseFloat(overridesByItem[it.item_id].discount_percent)).replace(/\.0+$/, '')}% off`
                                                    : overridesByItem[it.item_id].custom_rate != null
                                                        ? 'Custom rate'
                                                        : 'Quantity set by hand'}
                                                {Number(it.list_rate) > 0 && Number(it.list_rate) !== unit
                                                    ? ` — was ${formatMoney(it.list_rate)}`
                                                    : ''}
                                                {overridesByItem[it.item_id].reason
                                                    ? ` · “${overridesByItem[it.item_id].reason}”`
                                                    : ''}
                                            </div>
                                        )}
                                    </div>

                                    <div className="fees-right">
                                        {qtyWas != null && qtyWas !== qty && (
                                            <s className="fees-was">{String(qtyWas).replace(/\.0+$/, '')}</s>
                                        )}
                                        {String(qty).replace(/\.0+$/, '')}
                                    </div>
                                    <div className="fees-right">
                                        {overridesByItem[it.item_id] && Number(it.list_rate) > 0 && Number(it.list_rate) !== unit && (
                                            <s className="fees-was">{formatMoney(it.list_rate)}</s>
                                        )}
                                        {formatMoney(unit)}
                                    </div>

                                    {hasAnyDiscount && (
                                        <div className="fees-right">
                                            {Number.isFinite(discPct) && discPct > 0
                                                ? `${String(discPct).replace(/\.0+$/, '')}%`
                                                : '—'}
                                        </div>
                                    )}

                                    <div className="fees-right">
                                        {formatMoney(taxableAmount)}
                                    </div>

                                    <div className="fees-right">
                                        {hasAnyTax ? (
                                            taxRate > 0 ? (
                                                <>
                                                    {formatMoney(taxAmount)}{" "}
                                                    <span className="fees-tax-rate">
                                                        ({String(taxRate * 100).replace(/\.0+$/, '')}%)
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    {formatMoney(0)}{" "}
                                                    <span className="fees-tax-rate">(0%)</span>
                                                </>
                                            )
                                        ) : (
                                            "—"
                                        )}
                                    </div>

                                    <div className="fees-right fees-strong fees-total">{formatMoney(lineTotal)}</div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="fees-totals">
                        <div className="fees-total-row">
                            <span>Subtotal</span>
                            <span>{formatMoney(subtotal)}</span>
                        </div>

                        <div className="fees-total-row">
                            <span>VAT (5%)</span>
                            <span>{formatMoney(vat)}</span>
                        </div>

                        <div className="fees-total-row fees-grand fees-with-vat">
                            <strong>Total (incl. VAT)</strong>
                            <span className="fees-strong">{formatMoney(totalWithVat)}</span>
                        </div>
                    </div>
                </>
            ) : (
                <div className="fees-empty">
                    No fee breakdown found for this booking yet.
                </div>
            )}
        </section>
    );
}